import { FORMATS } from '@sonar-software/sonar-ts-constants';
import { getClosestCardHeader } from '@lib/popups';

import { DatePicker, Skeleton } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

export type DateRange = [Dayjs, Dayjs];

export type DateRangePickerParams = {
	end?: string;
	start?: string;
};

export enum DateRangePickerTypes {
	Date = 'date',
	Week = 'week',
	Month = 'month'
}

export interface DateRangePickerProps {
	disabled?: boolean;
	endDate?: string;
	getPopupContainer?: (trigger: HTMLElement) => HTMLElement;
	historyLimit?: number;
	loading?: boolean;
	maxDate?: Dayjs;
	minDate?: Dayjs;
	onChange: (params: DateRangePickerParams) => void;
	pickerType?: DateRangePickerTypes;
	startDate?: string;
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
	disabled,
	endDate,
	getPopupContainer,
	historyLimit,
	loading,
	maxDate,
	minDate,
	onChange,
	pickerType,
	startDate
}) => {
	const originalStart = dayjs(startDate || Date.now());
	const originalEnd = dayjs(endDate || Date.now());
	const [ dateRange, setDateRange ] = useState<DateRange>([ originalStart, originalEnd ]);

	useEffect(() => {
		if (pickerType === DateRangePickerTypes.Week) {
			let [ start, end ] = dateRange;

			start = dayjs(start).startOf('week');
			end = dayjs(end).endOf('week');

			setDateRange([ start, end ]);
		}
	}, [ pickerType ]);

	const startDateLimit = useMemo(
		() => (historyLimit ? dayjs().startOf('day')
			.subtract(historyLimit, 'day') : null),
		[ historyLimit ]
	);

	const handleIsDateDisabled = useCallback(
		(currentDate: Dayjs) => currentDate < (startDateLimit ?? 0),
		[ startDateLimit ]
	);

	const handleDateChange = useCallback((dates: DateRange) => {
		let [ start, end ] = dates;

		if (!start.isSame(originalStart) || !end.isSame(originalEnd)) {
			if (pickerType === DateRangePickerTypes.Week) {
				start = dayjs(start).startOf('week');
				end = dayjs(end).endOf('week');
			}

			onChange({
				end: end.format(FORMATS.SHORT_DATE),
				start: start.format(FORMATS.SHORT_DATE)
			});

			setDateRange([ start, end ]);
		}
	}, [ onChange, originalEnd, originalStart, pickerType ]);

	if (loading) {
		return <Skeleton />;
	}

	return (
		<DatePicker.RangePicker
			allowClear={ false }
			disabled={ disabled }
			disabledDate={ handleIsDateDisabled }
			format={ pickerType === DateRangePickerTypes.Week ? 'YYYY-MM-DD' : undefined }
			getPopupContainer={ getPopupContainer ?? getClosestCardHeader }
			maxDate={ maxDate }
			minDate={ minDate }
			onChange={ handleDateChange }
			picker={ pickerType }
			showTime={ false }
			value={ dateRange } />
	);
};
